import { SecurityInfo } from 'data/securityInfo/securityInfo.types';
import { SupportedFeaturesAccessFunctions } from 'data/supportedFeatures/supportedFeatures.types';
import {
  defaultTslOptions, DTLSversions, FormTemplate, FormTemplateFieldMap, FormTemplateMapByKey, FormTemplateSection,
} from 'data/template/template.types';
import _ from 'lodash';
import cloneDeep from 'lodash.clonedeep';
import { getTagOptions } from 'routes/AccessFunction/utils/accessFunctionTemplate.adapter';
import { padRow } from 'routes/TlsProfile/utils/tlsProfileTemplate.adapter';

const XCCA_FIELDS_TO_HIDE = ['certificate', 'certificateFile', 'certificateAuthority', 'certificateDirectory', 'privateKey', 'privateKeyFile', 'passphrase'];
const XCCA_FIELDS_TO_SHOW = ['xcca', 'keyalgorithm', 'aftype', 'litype'];

export function adaptTemplateOptions(
  template: FormTemplate,
  enabledAccessFunctions: SupportedFeaturesAccessFunctions,
  templates: FormTemplateMapByKey
): FormTemplate {
  if (template == null) {
    return template;
  }

  const clonedTemplate = cloneDeep(template);
  const tagOptions = getTagOptions(
    templates ?? {},
    enabledAccessFunctions ?? {}
  );
  (clonedTemplate.main as FormTemplateSection).fields.aftype.options?.push(
    ...tagOptions
  );
  return clonedTemplate;
}

export function adaptTemplateForFieldUpdate(
  data: Partial<SecurityInfo>,
  template: FormTemplate | undefined,
  fieldName: string,
  fieldValue: string,
): FormTemplate | undefined {
  if (template == null) {
    return;
  }
  if (
    fieldName !== '' &&
    fieldName !== 'tlsType' &&
    fieldName !== 'authenType' &&
    fieldName !== 'intfType' &&
    fieldName !== 'catype'
  ) {
    return;
  }
  const clonedTemplate = cloneDeep(template);
  clonedTemplate.formId = Date.now().toString();
  const templateSection = clonedTemplate.main as FormTemplateSection;
  const tlsTypeValue = fieldName === 'tlsType' ? fieldValue : data.tlsType;
  const anthenTypeValue = fieldName === 'authenType' ? fieldValue : data.authenType;
  const intfTypeValue = fieldValue === 'DTLS' ? fieldValue : 'default';

  if (fieldName === 'intfType') {
    adaptTemplateForDTLSType(templateSection, intfTypeValue);
  }

  adaptTemplateForHostname(templateSection, tlsTypeValue);

  adaptTemplateForDepth(templateSection, anthenTypeValue);

  adaptTemplateForRequiredFields(
    templateSection,
    tlsTypeValue,
    anthenTypeValue,
  );

  if (fieldName === 'catype') {
    if (fieldValue === 'XCCA') {
      XCCA_FIELDS_TO_HIDE.forEach((fName) => {
        if (templateSection.fields[fName]) {
          templateSection.fields[fName].hide = 'true';
        }
      });
      XCCA_FIELDS_TO_SHOW.forEach((fName) => {
        if (templateSection.fields[fName]) {
          templateSection.fields[fName].hide = 'false';
        }
      });
    } else {
      XCCA_FIELDS_TO_HIDE.forEach((fName) => {
        if (templateSection.fields[fName]) {
          templateSection.fields[fName].hide = 'false';
        }
      });
      XCCA_FIELDS_TO_SHOW.forEach((fName) => {
        if (templateSection.fields[fName]) {
          templateSection.fields[fName].hide = 'true';
        }
      });
    }
  }

  if (
    templateSection.metadata.layout &&
    _.isArray(templateSection.metadata.layout.rows)
  ) {
    templateSection.metadata.layout.rows[1] = padRow(templateSection.fields, templateSection.metadata.layout.rows[1], 3);
    templateSection.metadata.layout.rows[2] = padRow(templateSection.fields, templateSection.metadata.layout.rows[2], 3);
  }

  return clonedTemplate;
}

function adaptTemplateForDTLSType(section: FormTemplateSection | undefined, fieldValue: string): void {
  if (!section) {
    return;
  }

  if (fieldValue === 'DTLS') {
    section.fields.tlsVersion.options = DTLSversions;
  } else {
    section.fields.tlsVersion.options = defaultTslOptions;
  }
}

function adaptTemplateForHostname(
  templateSection: FormTemplateSection | undefined,
  tlsTypeValue: string | undefined,
): void {
  if (templateSection == null) {
    return;
  }

  const { hostname } = templateSection.fields;

  if (hostname == null) {
    return;
  }

  if (tlsTypeValue === 'Client') {
    hostname.disabled = false;
  } else {
    hostname.disabled = true;
  }
}

function adaptTemplateForDepth(
  templateSection: FormTemplateSection | undefined,
  authenTypeValue: string | undefined,
): void {
  if (templateSection == null) {
    return;
  }

  const { depth } = templateSection.fields;

  if (depth == null) {
    return;
  }

  if (authenTypeValue === 'MUTUAL') {
    depth.disabled = false;
  } else {
    depth.disabled = true;
  }
}

function adaptTemplateForRequiredFields(
  templateSection: FormTemplateSection | undefined,
  tlsTypeValue: string | undefined,
  authenTypeValue: string | undefined,
): void {
  if (templateSection == null) {
    return;
  }

  const {
    privateKeyFile,
    passphrase,
    certificateFile,
    certificateAuthority,
  } = templateSection.fields;

  if (
    privateKeyFile == null ||
    passphrase == null ||
    certificateFile == null ||
    certificateAuthority == null
  ) {
    return;
  }

  const isPrivateKeyFileRequired =
    authenTypeValue === 'MUTUAL' || tlsTypeValue === 'Server';
  const isPassphraseRequired = authenTypeValue === 'MUTUAL';
  const isCertificateFileRequired =
    authenTypeValue === 'MUTUAL' || tlsTypeValue === 'Server';
  const isCertificateAuthorityRequired =
    authenTypeValue === 'MUTUAL' || tlsTypeValue === 'Client';

  privateKeyFile.validation = {
    ...privateKeyFile.validation,
    required: isPrivateKeyFileRequired,
  };
  passphrase.validation = {
    ...passphrase.validation,
    required: isPassphraseRequired,
  };
  certificateFile.validation = {
    ...certificateFile.validation,
    required: isCertificateFileRequired,
  };
  certificateAuthority.validation = {
    ...certificateAuthority.validation,
    required: isCertificateAuthorityRequired,
  };
}
